import { DatePipe, DecimalPipe } from '@angular/common';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, importProvidersFrom, LOCALE_ID } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import icons from '@common/classes/icons';
import { AuthGuard } from '@common/guards/auth.guard';
import { DialogErrorHandler } from '@common/handlers/dialog-error-handler';
import { appHttpInterceptor } from '@common/interceptors/http-interceptor';
import { CodelistPipe } from '@common/pipes/codelist.pipe';
import { CustomDatePipe } from '@common/pipes/date.pipe';
import { DateTimePipe } from '@common/pipes/datetime.pipe';
import { DarkModeService } from '@common/services/dark-mode.service';
import { DialogService } from '@common/services/dialog.service';
import { I18nService } from '@common/services/i18n.service';
import { RememberStateService } from '@common/services/remember-state.service';
import { ToastrNotificationService } from '@common/services/toastr-notification.service';
import { WebsocketService } from '@common/services/websocket.service';
import { WindowFocusService } from '@common/services/window-focus.service';
import { environment } from '@environments/environment';
import { provideIcons, provideNgIconsConfig, withContentSecurityPolicy, withExceptionLogger } from '@ng-icons/core';
import { provideQueryDevTools } from '@ngneat/query-devtools';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { IntlModule } from '@progress/kendo-angular-intl';
import { CommonService } from '@services/common.service';
import { QueryService } from '@services/query.service';
import { SettingsService } from '@services/settings.service';
import { SharedService } from '@services/shared.service';
import { UserService } from '@services/user.service';
import { ToastrModule } from 'ngx-toastr';
import { ROUTES } from './app.routing';

export const appConfig: ApplicationConfig = {
    providers: [
        // HTTP Client - must be first
        provideHttpClient(withInterceptorsFromDi(), withInterceptors([appHttpInterceptor])),

        // Router
        provideRouter(ROUTES, withComponentInputBinding()),

        // Locale
        { provide: LOCALE_ID, useValue: 'sl' },

        // Core Services
        UserService,
        QueryService,
        CommonService,
        SettingsService,
        SharedService,
        WebsocketService,
        RememberStateService,
        ToastrNotificationService,
        DialogService,
        DarkModeService,
        I18nService,
        WindowFocusService,

        // Pipes
        CodelistPipe,
        DatePipe,
        CustomDatePipe,
        DateTimePipe,
        DecimalPipe,

        // Guards
        AuthGuard,

        // Development tools
        ...(environment.name === 'local' ? [provideQueryDevTools({ buttonPosition: 'bottom-right' })] : []),

        // NgIcons
        provideNgIconsConfig({}, withContentSecurityPolicy(), withExceptionLogger()),
        provideIcons(icons),
        // Module providers
        importProvidersFrom(BrowserAnimationsModule, LoadingBarModule, IntlModule, ToastrModule.forRoot()),
        provideTranslateService({
            fallbackLang: environment.defaultLanguage,
            loader: provideTranslateHttpLoader({
                prefix: './assets/i18n/',
                suffix: '.json'
            })
        }),

        // Error Handler
        { provide: ErrorHandler, useClass: DialogErrorHandler }
    ]
};
